import { ChangeEvent, FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '../Icon/Icon';
import { ISelectMiniOption, SelectMini } from '../SelectMini/SelectMini';

const StyledPaginationContainer = styled.div`
  display: flex;
`;

const StyledPageSizeOptions = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  button {
    background-color: transparent;
    border: 0;
    cursor: pointer;
    svg {
      color: ${({ theme }) => theme.vars.newColor.darkGS1};
    }
    :disabled {
      cursor: default;
      svg {
        color: ${({ theme }) => theme.vars.newColor.darkGS3};
      }
    }
  }
  input {
    width: 32px;
    height: 32px;
    outline: none;
    border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
    text-align: center;
  }
  .center {
    margin: 0 10px;
    .pageInfo {
      display: flex;
      align-items: center;
      .pageJump {
        margin: 0 16px;
      }
      .ofText {
        margin-right: 5px;
      }
    }
  }
`;

interface IRenderPageJumpProps {
  onKeyPress: (e: any) => void;
  onBlur: (e: any) => void;
  value: number | string;
  onChange: (e: any) => void;
  pageJumpText: string;
}

export interface IPaginationProps {
  page: number;
  pages: number;
  onPageChange: (page: number) => void;
  pageJumpText: string;
  showPageSizeOptions: boolean;
  pageSizeOptions: number[];
  pageSize: number;
  showPageJump: boolean;
  canPrevious: boolean;
  canNext: boolean;
  onPageSizeChange: () => void;
  className: string;
  renderPageJump?: (props: IRenderPageJumpProps) => void;
  renderCurrentPage?: (page: number) => void;
  renderTotalPagesCount?: (page: number) => void;
  renderPageSizeOptions?: (props: any) => void;
  rowsSelectorText: string;
  rowsText: string;
  pageText: string;
  ofText: string;
}

const defaultButton = props => (
  <button type="button" {...props} className="-btn">
    {props.children}
  </button>
);

const defaultProps = {
  PreviousComponent: defaultButton,
  NextComponent: defaultButton,
  // eslint-disable-next-line react/display-name
  renderPageJump: ({ onChange, value, onBlur, onKeyPress, inputType, pageJumpText }) => (
    <div className="pageJump">
      <input
        aria-label={pageJumpText}
        type={inputType}
        onChange={onChange}
        value={value}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        data-testid="PaginationPageJump"
      />
    </div>
  ),
  // eslint-disable-next-line react/display-name
  renderCurrentPage: (page: number) => <span className="currentPage">{page + 1}</span>,
  // eslint-disable-next-line react/display-name
  renderTotalPagesCount: (pages: number) => <span className="totalPages">{pages || 1}</span>,
  // eslint-disable-next-line react/display-name
  renderPageSizeOptions: ({ pageSize, pageSizeOptions, onPageSizeChange, rowsText }) => {
    const options: ISelectMiniOption[] = pageSizeOptions.map((option: string) => ({
      value: option,
      label: `${option} ${rowsText}`,
    }));

    const value = options.find(option => option.value === pageSize);

    return (
      <StyledPageSizeOptions>
        <SelectMini
          ariaLabel="Number of rows"
          options={options}
          menuPlacement="bottom"
          value={value}
          onChange={selectedOption => onPageSizeChange(Number(selectedOption.value))}
        />
      </StyledPageSizeOptions>
    );
  },
};

const Pagination: FC<IPaginationProps> = props => {
  const {
    // Computed
    pages,
    // Props
    page,
    showPageSizeOptions,
    pageSizeOptions,
    pageSize,
    showPageJump,
    canPrevious,
    canNext,
    onPageSizeChange,
    renderPageJump,
    renderCurrentPage,
    renderTotalPagesCount,
    renderPageSizeOptions,
  } = props;

  const [pageInput, setPageInput] = useState<string | number>('');

  const getSafePage = newPage => {
    if (Number.isNaN(newPage)) {
      newPage = props.page;
    }
    return Math.min(Math.max(newPage, 0), props.pages - 1);
  };

  const changePage = newPage => {
    newPage = getSafePage(newPage);
    setPageInput(newPage);
    if (props.page !== newPage) {
      props.onPageChange(newPage);
    }
  };

  const applyPage = (e: KeyboardEvent) => {
    if (e) {
      e.preventDefault();
    }
    changePage(pageInput === '' ? props.page : pageInput);
  };

  const getPageJumpProperties = () => {
    return {
      onKeyPress: (e: KeyboardEvent) => {
        if (e.which === 13 || e.keyCode === 13) {
          applyPage(e);
        }
      },
      onBlur: applyPage,
      value: pageInput === '' ? pageInput : Number(pageInput) + 1,
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        const val: any = e.target.value;
        if (val === '') {
          return setPageInput(val);
        }
        const newPage = val - 1;
        setPageInput(getSafePage(newPage));
      },
      pageJumpText: props.pageJumpText,
    };
  };

  useEffect(() => {
    setPageInput(page);
  }, [page]);

  return (
    <StyledPaginationContainer>
      {showPageSizeOptions &&
        renderPageSizeOptions({
          pageSize,
          rowsSelectorText: props.rowsSelectorText,
          pageSizeOptions,
          onPageSizeChange,
          rowsText: props.rowsText,
        })}
      <StyledNavigation>
        <div className="previous">
          <button
            onClick={() => {
              if (!canPrevious) {
                return;
              }
              changePage(0);
            }}
            disabled={!canPrevious}
            data-testid="PaginationStartButton"
          >
            <Icon icon="angle-double-left" />
          </button>
          <button
            onClick={() => {
              if (!canPrevious) {
                return;
              }
              changePage(page - 1);
            }}
            disabled={!canPrevious}
            data-testid="PaginationPrevButton"
          >
            <Icon icon="angle-left" />
          </button>
        </div>
        <div className="center">
          <span className="pageInfo">
            {showPageJump ? renderPageJump(getPageJumpProperties()) : renderCurrentPage(page)}
            <span className="ofText">{props.ofText}</span> {renderTotalPagesCount(pages)}
          </span>
        </div>
        <div className="next">
          <button
            onClick={() => {
              if (!canNext) {
                return;
              }
              changePage(page + 1);
            }}
            disabled={!canNext}
            data-testid="PaginationNextButton"
          >
            <Icon icon="angle-right" />
          </button>
          <button
            onClick={() => {
              if (!canNext) {
                return;
              }
              changePage(pages);
            }}
            disabled={!canNext}
            data-testid="PaginationEndButton"
          >
            <Icon icon="angle-double-right" size="medium" />
          </button>
        </div>
      </StyledNavigation>
    </StyledPaginationContainer>
  );
};

Pagination.defaultProps = defaultProps;

export default Pagination;
